import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Lock, PlayCircle, FileText, Video, Award, Pencil, Send, Archive, Download, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { coverUrl, downloadFile } from "@/lib/training";
import { RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";
import { DeleteButton } from "@/components/DeleteButton";
import { ProgressBar } from "@/components/training/TrainingBits";
import { RelatedForms } from "@/components/forms/RelatedForms";

const TYPE_ICON = { text: FileText, video: Video, pdf: FileText };

export default function CourseOverview() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);
  useEffect(load, [load]);

  const setStatus = async (status) => {
    try {
      await api.post(`/courses/${id}/status`, { status });
      toast.success(`Course ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) return <EmptyState title="Course not available" description={error} testId="course-error" />;
  if (!course) return null;
  const p = course.progress;
  const done = new Set(p?.completed_lesson_ids || []);
  const resumeId = p?.last_lesson_id && !done.has(p.last_lesson_id) ? p.last_lesson_id : course.lessons.find((l) => !done.has(l.id))?.id || course.lessons[0]?.id;
  const allDone = course.quiz.unlocked;
  const cta = p?.status === "completed" ? null : allDone && course.quiz.question_count ? { to: `/courses/${id}/quiz`, label: "Take the quiz" } : resumeId && { to: `/courses/${id}/learn/${resumeId}`, label: p ? "Resume course" : "Start course" };

  return (
    <div className="fade-up">
      <Link to="/courses" className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="course-back-link"><ArrowLeft className="h-4 w-4" /> Courses</Link>
      <div className="mt-8 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep">{course.category}</span>
            {isAdmin && <StatusBadge status={course.status} testId="course-status" />}
            <span className="font-mono text-xs text-zinc-500">v{course.version}</span>
          </div>
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl" data-testid="course-title">{course.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-zinc-600">{course.description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-zinc-500">
            {course.applicable_roles.map((r) => <RoleBadge key={r} role={r} />)}
            <span className="ml-2">{course.lessons.length} lessons · {course.estimated_minutes} min · pass mark {course.quiz.pass_mark}%</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {cta && (
              <Link to={cta.to} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-6 py-3 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="course-start-button">
                <PlayCircle className="h-4 w-4" /> {cta.label}
              </Link>
            )}
            {p?.certificate_code && (
              <button onClick={() => downloadFile(`/certificates/${p.certificate_code}/pdf`, `Lasso-Hub-${p.certificate_code}.pdf`)} className="inline-flex items-center gap-2 rounded-full bg-lasso-sky px-6 py-3 text-sm font-semibold text-lasso-ink hover:bg-lasso-sky/80" data-testid="course-certificate-download">
                <Download className="h-4 w-4" /> Download certificate
              </button>
            )}
            {course.can_edit && (
              <>
                <Link to={`/courses/${id}/edit`} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold hover:border-lasso-ink" data-testid="course-edit-button"><Pencil className="h-4 w-4" /> Edit</Link>
                {course.status !== "published" && <button onClick={() => setStatus("published")} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold hover:border-lasso-ink" data-testid="course-publish-button"><Send className="h-4 w-4" /> Publish</button>}
                {course.status === "published" && <button onClick={() => setStatus("archived")} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold hover:border-lasso-ink" data-testid="course-archive-button"><Archive className="h-4 w-4" /> Archive</button>}
                {isAdmin && <DeleteButton path={`/courses/${id}`} name={course.title} what="Course" redirect="/courses" testId="course-delete-button" />}
              </>
            )}
          </div>
          {p && (
            <div className="mt-8 max-w-md" data-testid="course-progress">
              <div className="mb-2 flex justify-between text-xs font-semibold"><span>Your progress</span><span data-testid="course-progress-pct">{p.pct}%</span></div>
              <ProgressBar pct={p.pct} />
              {p.attempts > 0 && <p className="mt-3 text-xs text-muted-foreground" data-testid="course-quiz-scores">Quiz: {p.attempts} attempt{p.attempts > 1 ? "s" : ""} · best {p.best_score}% · last {p.last_score}%</p>}
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded-3xl bg-lasso-tint">
          {coverUrl(course) && <img src={coverUrl(course)} alt="" className="aspect-[4/3] h-full w-full object-cover" />}
        </div>
      </div>

      <div className="mt-14 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <section>
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Lessons</h2>
          <ol className="divide-y divide-lasso-line overflow-hidden rounded-2xl border border-lasso-line bg-white" data-testid="course-lesson-list">
            {course.lessons.map((l, i) => {
              const Icon = TYPE_ICON[l.type];
              return (
                <li key={l.id}>
                  <Link to={`/courses/${id}/learn/${l.id}`} className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-lasso-tint" data-testid={`course-lesson-${i + 1}`}>
                    {done.has(l.id) || p?.status === "completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-zinc-300" />}
                    <span className="font-mono text-xs text-zinc-400">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1 text-sm font-medium">{l.title}</span>
                    <span className="inline-flex items-center gap-1 text-xs capitalize text-zinc-500"><Icon className="h-3.5 w-3.5" />{l.type} · {l.minutes}m</span>
                  </Link>
                </li>
              );
            })}
            {course.quiz.question_count > 0 && (
              <li className="flex items-center gap-4 px-5 py-4" data-testid="course-quiz-row">
                {p?.status === "completed" ? <Award className="h-5 w-5 text-emerald-600" /> : allDone ? <Award className="h-5 w-5 text-lasso-deep" /> : <Lock className="h-5 w-5 text-zinc-300" />}
                <span className="flex-1 text-sm font-medium">Final quiz · {course.quiz.question_count} questions</span>
                {allDone ? <Link to={`/courses/${id}/quiz`} className="text-xs font-semibold underline" data-testid="course-quiz-link">{p?.status === "completed" ? "Retake" : "Start"}</Link> : <span className="text-xs text-zinc-400">Unlocks after all lessons</span>}
              </li>
            )}
          </ol>
        </section>
        {course.linked_sops.length > 0 && (
          <section>
            <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Related SOPs</h2>
            <div className="space-y-2" data-testid="course-linked-sops">
              {course.linked_sops.map((s) => (
                <Link key={s.id} to={`/sops/${s.id}`} className="flex items-center gap-3 rounded-xl border border-lasso-line bg-white px-4 py-3 text-sm font-medium hover:border-lasso-ink">
                  <BookOpen className="h-4 w-4 text-lasso-deep" /> {s.title}
                </Link>
              ))}
            </div>
          </section>
        )}
        <RelatedForms kind="courses" id={id} />
      </div>
    </div>
  );
}
